(function (root, factory) {
  'use strict';
  var api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root && root.document) api.mount(root, root.document);
})(typeof window !== 'undefined' ? window : null, function () {
  'use strict';

  var CONFIG_SELECTOR = '[data-mdv-inventory-config]';
  var PRODUCT_SELECTOR = '[data-mdv-inventory-product]';
  var COLLECTION_PROPERTY = '_mdv_collection_location';
  var CACHE_TTL_MS = 10000;

  function text(value) { return value === undefined || value === null ? '' : String(value); }
  function parseJSON(value) { try { return JSON.parse(value || ''); } catch (error) { return null; } }
  function own(object, key) { return Boolean(object && Object.prototype.hasOwnProperty.call(object, key)); }
  function bool(value) {
    if (value === true || value === 'true') return true;
    if (value === false || value === 'false') return false;
    return null;
  }
  function list(value) { return Array.isArray(value) ? value.map(text).filter(Boolean) : []; }

  function normalizeConfig(value) {
    if (!value || typeof value !== 'object') return null;
    var result = {
      endpoint: text(value.endpoint),
      shop: text(value.shop),
      deliveryIds: list(value.deliveryIds),
      retailIds: list(value.retailIds),
      deliveryClassifications: list(value.deliveryClassifications),
      retailClassifications: list(value.retailClassifications),
      collectionLocation: text(value.collectionLocation),
      timeoutMs: Math.max(500, Number(value.timeoutMs) || 3000),
      labels: value.labels && typeof value.labels === 'object' ? value.labels : {}
    };
    if (!/^https:\/\//.test(result.endpoint) || !result.shop || !result.deliveryIds.length ||
        !result.retailIds.length || !result.deliveryClassifications.length ||
        !result.retailClassifications.length || !result.collectionLocation) return null;
    return result;
  }

  function normalizeInput(value) {
    if (!value || typeof value !== 'object') return null;
    return {
      productId: text(value.productId),
      variantId: text(value.variantId),
      handle: text(value.handle),
      tracked: bool(value.tracked),
      policy: text(value.policy).toLowerCase(),
      preorder: value.preorder === true,
      giftCard: value.giftCard === true,
      requiresShipping: bool(value.requiresShipping)
    };
  }

  function isNeutral(input) {
    return Boolean(input && (input.giftCard === true || input.requiresShipping === false));
  }

  function isDeliveryByPolicy(input) {
    return Boolean(input && !isNeutral(input) &&
      (input.preorder === true || input.tracked === false || input.policy === 'continue'));
  }

  function exact(value, values) { return typeof value === 'string' && values.indexOf(value) !== -1; }

  function classifyStock(input, deliveryValue, retailValue, config) {
    input = normalizeInput(input);
    if (!input) return { kind: 'unknown', reason: 'input-missing' };
    if (isNeutral(input)) return { kind: 'neutral' };
    if (isDeliveryByPolicy(input)) return { kind: 'delivery', policyBypass: true };
    if (!input.productId || !input.variantId || input.tracked !== true || input.policy !== 'deny') {
      return { kind: 'unknown', reason: 'metadata-incomplete' };
    }
    if (exact(deliveryValue, config.deliveryClassifications)) return { kind: 'delivery' };
    if (deliveryValue !== false) return { kind: 'unknown', reason: 'delivery-response-invalid' };
    if (exact(retailValue, config.retailClassifications)) return { kind: 'collection' };
    if (retailValue === false) return { kind: 'unavailable' };
    return { kind: 'unknown', reason: 'retail-response-invalid' };
  }

  function responseMap(payload, productId) {
    if (!payload || payload.errors || Number(payload.status) !== 200 || !payload.data ||
        typeof payload.data !== 'object') return null;
    var map = payload.data[text(productId)];
    return map && typeof map === 'object' && !Array.isArray(map) ? map : null;
  }

  function requestUrl(config, productId, locationIds) {
    var url = new URL(config.endpoint);
    url.searchParams.set('shop', config.shop);
    url.searchParams.set('location_id', locationIds.join(','));
    url.searchParams.set('product_id', text(productId));
    return url.toString();
  }

  function withDeadline(promise, timeoutMs, controller) {
    var timer;
    var timeout = new Promise(function (_, reject) {
      timer = setTimeout(function () {
        if (controller) controller.abort();
        var error = new Error('inventory-timeout');
        error.mdvInventoryOutage = true;
        reject(error);
      }, timeoutMs);
    });
    return Promise.race([promise, timeout]).finally(function () { clearTimeout(timer); });
  }

  function createStockService(configValue, options) {
    var config = normalizeConfig(configValue);
    var fetchImpl = options && options.fetch;
    var now = options && options.now || Date.now;
    var AbortControllerImpl = options && own(options, 'AbortController') ? options.AbortController :
      (typeof AbortController === 'function' ? AbortController : null);
    if (!config || typeof fetchImpl !== 'function') throw new TypeError('Valid inventory service configuration required.');
    var caches = { delivery: new Map(), retail: new Map() };

    function loadMap(kind, productId, force) {
      var key = [config.shop, kind === 'delivery' ? config.deliveryIds.join(',') : config.retailIds.join(','), text(productId)].join('|');
      var cache = caches[kind];
      var existing = cache.get(key);
      var stamp = Number(now());
      if (!force && existing && existing.value && stamp - existing.at <= CACHE_TTL_MS) return Promise.resolve(existing.value);
      if (existing && existing.promise) return existing.promise;
      var controller = typeof AbortControllerImpl === 'function' ? new AbortControllerImpl() : null;
      var requestOptions = { method: 'GET', mode: 'cors', credentials: 'omit', headers: { Accept: 'application/json' } };
      if (controller) requestOptions.signal = controller.signal;
      var ids = kind === 'delivery' ? config.deliveryIds : config.retailIds;
      var request = withDeadline(Promise.resolve().then(function () {
          return fetchImpl(requestUrl(config, productId, ids), requestOptions);
        }).catch(function (error) {
          if (error && typeof error === 'object') error.mdvInventoryOutage = true;
          throw error;
        })
        .then(function (response) {
          if (!response || !response.ok) {
            var error = new Error('inventory-response');
            error.mdvInventoryOutage = true;
            throw error;
          }
          return response.json();
        })
        .then(function (payload) {
          var map = responseMap(payload, productId);
          if (!map) throw new Error('inventory-payload');
          return map;
        }), config.timeoutMs, controller)
        .then(function (map) {
          cache.set(key, { value: map, at: Number(now()) });
          return map;
        })
        .catch(function (error) {
          cache.delete(key);
          throw error;
        });
      cache.set(key, { promise: request, at: stamp });
      return request;
    }

    function mapValue(kind, input, force) {
      return loadMap(kind, input.productId, force).then(function (map) {
        if (!own(map, input.variantId)) return { ok: false, reason: kind + '-variant-missing' };
        return { ok: true, value: map[input.variantId] };
      });
    }

    function check(value, optionsValue) {
      var input = normalizeInput(value);
      if (!input) return Promise.resolve({ kind: 'unknown', reason: 'input-missing' });
      if (isNeutral(input)) return Promise.resolve({ kind: 'neutral' });
      if (isDeliveryByPolicy(input)) return Promise.resolve({ kind: 'delivery', policyBypass: true });
      if (!input.productId || !input.variantId || input.tracked !== true || input.policy !== 'deny') {
        return Promise.resolve({ kind: 'unknown', reason: 'metadata-incomplete' });
      }
      var force = Boolean(optionsValue && optionsValue.force);
      return mapValue('delivery', input, force).then(function (delivery) {
        if (!delivery.ok) return { kind: 'unknown', reason: delivery.reason };
        if (exact(delivery.value, config.deliveryClassifications)) return { kind: 'delivery' };
        if (delivery.value !== false) return { kind: 'unknown', reason: 'delivery-response-invalid' };
        return mapValue('retail', input, force).then(function (retail) {
          if (!retail.ok) return { kind: 'unknown', reason: retail.reason };
          return classifyStock(input, delivery.value, retail.value, config);
        });
      }).catch(function (error) {
        return { kind: 'unknown', reason: error && error.message || 'inventory-request-failed', outage: Boolean(error && error.mdvInventoryOutage) };
      });
    }

    function checkCollection(value, optionsValue) {
      var input = normalizeInput(value);
      if (!input || !input.productId || !input.variantId) return Promise.resolve({ kind: 'unknown', reason: 'metadata-incomplete' });
      return mapValue('retail', input, Boolean(optionsValue && optionsValue.force)).then(function (retail) {
        if (!retail.ok) return { kind: 'unknown', reason: retail.reason };
        if (exact(retail.value, config.retailClassifications)) return { kind: 'collection' };
        if (retail.value === false) return { kind: 'unavailable' };
        return { kind: 'unknown', reason: 'retail-response-invalid' };
      }).catch(function (error) {
        return { kind: 'unknown', reason: error && error.message || 'inventory-request-failed', outage: Boolean(error && error.mdvInventoryOutage) };
      });
    }

    return {
      check: check,
      checkCollection: checkCollection,
      clear: function () { caches.delivery.clear(); caches.retail.clear(); },
      config: config
    };
  }

  function variantFromProductJson(product, item) {
    if (!product || !item || text(product.id) !== text(item.product_id) || !Array.isArray(product.variants)) return null;
    var variant = product.variants.find(function (candidate) { return text(candidate && candidate.id) === text(item.variant_id || item.id); });
    if (!variant || !own(variant, 'inventory_management') || !own(variant, 'inventory_policy') || !own(variant, 'requires_shipping')) return null;
    return normalizeInput({
      productId: product.id,
      variantId: variant.id,
      handle: product.handle || item.handle,
      tracked: variant.inventory_management === 'shopify' ? true : (variant.inventory_management === null ? false : null),
      policy: variant.inventory_policy,
      preorder: Boolean(item.properties && item.properties._pre_order),
      giftCard: item.gift_card === true,
      requiresShipping: variant.requires_shipping
    });
  }

  function collectionProperty(item, config) {
    return text(item && item.properties && item.properties[COLLECTION_PROPERTY]) === config.collectionLocation;
  }

  function cartMode(results) {
    var physical = results.filter(function (result) { return result.kind !== 'neutral'; });
    if (!physical.length) return { kind: 'empty' };
    if (physical.some(function (result) { return result.kind === 'unknown' || result.kind === 'unavailable'; })) return { kind: 'unknown' };
    var delivery = physical.some(function (result) { return result.kind === 'delivery'; });
    var collection = physical.some(function (result) { return result.kind === 'collection'; });
    if (delivery && collection) return { kind: 'conflict' };
    if (collection) return { kind: 'collection' };
    return delivery ? { kind: 'delivery' } : { kind: 'unknown' };
  }

  function decideCompatibility(action, candidate, mode) {
    if (candidate.kind === 'neutral') return { allow: true, action: 'neutral' };
    if (action === 'delivery' && candidate.kind === 'collection') return { allow: false, kind: 'collection_available' };
    if (candidate.kind === 'unavailable') return { allow: false, kind: 'unavailable' };
    if (candidate.kind === 'unknown') {
      if (action === 'delivery' && candidate.outage === true && mode.kind === 'empty') return { allow: true, action: 'delivery', degraded: true };
      return { allow: false, kind: 'retry' };
    }
    if (mode.kind === 'unknown' || mode.kind === 'conflict') return { allow: false, kind: 'retry' };
    if (action === 'collection') {
      if (candidate.kind !== 'collection') return { allow: false, kind: candidate.kind === 'delivery' ? 'collection_changed' : 'retry' };
      return mode.kind === 'empty' || mode.kind === 'collection' ?
        { allow: true, action: 'collection' } : { allow: false, kind: 'delivery_bag' };
    }
    if (candidate.kind !== 'delivery') return { allow: false, kind: 'retry' };
    return mode.kind === 'empty' || mode.kind === 'delivery' ?
      { allow: true, action: 'delivery' } : { allow: false, kind: 'collection_bag' };
  }

  function writeOutcomeAmbiguous(value) {
    var kind = text(value && value.kind);
    return kind === 'uncertain' || kind === 'uncertain_context_mismatch' ||
      kind === 'confirmed_context_mismatch' || kind === 'confirmed_stale';
  }

  function businessWriteError(error) {
    var status = Number(error && error.status);
    var body = error && error.responseJSON;
    if ((status !== 400 && status !== 422) || !body || typeof body !== 'object' || Array.isArray(body)) return null;
    if (body.status !== undefined && Number(body.status) !== status) return null;
    var message = typeof body.description === 'string' && body.description.trim() ||
      (typeof body.message === 'string' && body.message.trim()) || '';
    return message ? { status: status, message: message } : null;
  }

  function cartWriteSignature(cart) {
    if (!cart || !Array.isArray(cart.items)) return null;
    var lines = [];
    for (var index = 0; index < cart.items.length; index += 1) {
      var item = cart.items[index];
      if (!item || typeof item !== 'object' || Array.isArray(item) ||
          !Number.isSafeInteger(Number(item.quantity)) || Number(item.quantity) < 0) return null;
      var identity = text(item.key || item.variant_id || item.id);
      if (!identity) return null;
      var properties = item.properties && typeof item.properties === 'object' && !Array.isArray(item.properties) ? item.properties : {};
      var normalizedProperties = {};
      Object.keys(properties).sort().forEach(function (key) { normalizedProperties[key] = text(properties[key]); });
      lines.push({ identity: identity, quantity: Number(item.quantity), properties: normalizedProperties });
    }
    lines.sort(function (left, right) {
      var first = left.identity.localeCompare(right.identity);
      return first || JSON.stringify(left.properties).localeCompare(JSON.stringify(right.properties));
    });
    return JSON.stringify(lines);
  }

  function resetOwnedAddButton(button, currentVariant, nativeLabel) {
    if (!button || !button.dataset) return false;
    var current = text(currentVariant);
    var busyStale = Boolean(button.dataset.mdvInventoryVariant && button.dataset.mdvInventoryVariant !== current);
    var collectionStale = Boolean(button.dataset.mdvCollectionReady && button.dataset.mdvCollectionReady !== current);
    if (!busyStale && !collectionStale) return false;
    if (busyStale) {
      delete button.dataset.mdvInventoryBusy;
      delete button.dataset.mdvInventoryVariant;
      button.disabled = false;
    }
    if (collectionStale) delete button.dataset.mdvCollectionReady;
    button.value = text(nativeLabel);
    button.dataset.mdvNativeLabel = text(nativeLabel);
    return true;
  }

  function createGate(configValue, options) {
    var config = normalizeConfig(configValue);
    var fetchImpl = options && options.fetch;
    var rootPath = options && options.rootPath || '/';
    var service = createStockService(config, options);
    var AbortControllerImpl = options && own(options, 'AbortController') ? options.AbortController :
      (typeof AbortController === 'function' ? AbortController : null);
    var productCache = new Map();
    var queue = Promise.resolve();
    var recoveryRequired = false;

    function fetchJSON(url, requestOptions) {
      var controller = typeof AbortControllerImpl === 'function' ? new AbortControllerImpl() : null;
      var settings = Object.assign({}, requestOptions || { headers: { Accept: 'application/json' } });
      if (controller) settings.signal = controller.signal;
      return withDeadline(Promise.resolve(fetchImpl(url, settings)).then(function (response) {
        if (!response || !response.ok) throw new Error('read-' + (response && response.status || 'failed'));
        return response.json();
      }), Math.max(config.timeoutMs, 5000), controller);
    }

    function readCart() {
      return fetchJSON(rootPath + 'cart.js?mdv_inventory=' + Date.now(), {
        credentials: 'same-origin', headers: { Accept: 'application/json' }
      });
    }

    function loadProduct(handle, force) {
      handle = text(handle);
      if (!handle) return Promise.resolve(null);
      var existing = productCache.get(handle);
      if (existing) return existing;
      var promise = fetchJSON(rootPath + 'products/' + encodeURIComponent(handle) +
        '?view=mdv-inventory-json&mdv_inventory=' + Date.now(), {
        credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'text/html' }
      }).catch(function () { return null; });
      productCache.set(handle, promise);
      return promise;
    }

    function classifyCartItem(item) {
      if (!item || item.requires_shipping === false || item.gift_card === true) return Promise.resolve({ kind: 'neutral' });
      return loadProduct(item.handle, false).then(function (product) {
        var input = variantFromProductJson(product, item);
        if (!input) return { kind: 'unknown', reason: 'cart-metadata-incomplete' };
        if (collectionProperty(item, config)) {
          return service.checkCollection(input).then(function (result) {
            return result.kind === 'collection' ? { kind: 'collection', explicit: true } : result;
          });
        }
        return service.check(input).then(function (result) {
          if (result.kind === 'collection') result.legacy = true;
          return result;
        });
      });
    }

    function classifyCart(cart) {
      var items = cart && Array.isArray(cart.items) ? cart.items : null;
      if (!items) return Promise.resolve({ kind: 'unknown' });
      return Promise.all(items.map(classifyCartItem)).then(cartMode);
    }

    function run(request, writeFn) {
      var input = normalizeInput(request && request.input);
      var action = request && request.action === 'collection' ? 'collection' : 'delivery';
      if (!input || typeof writeFn !== 'function') return Promise.resolve({ kind: 'retry' });
      if (recoveryRequired) return Promise.resolve({ kind: 'reconcile_required' });
      service.clear();
      productCache.clear();
      return readCart().then(function (cart) {
        return Promise.all([
          action === 'collection' ? service.checkCollection(input) : service.check(input),
          classifyCart(cart)
        ]).then(function (values) {
          var decision = decideCompatibility(action, values[0], values[1]);
          if (!decision.allow) return decision;
          if (typeof request.isCurrent === 'function' && request.isCurrent() !== true) return { kind: 'stale' };
          var properties = Object.assign({}, request.properties || {});
          if (decision.action === 'collection') properties[COLLECTION_PROPERTY] = config.collectionLocation;
          var writeResult;
          try { writeResult = writeFn({ action: decision.action, properties: properties, degraded: decision.degraded === true }); }
          catch (error) { writeResult = Promise.reject(error); }
          return Promise.resolve(writeResult).then(function (value) {
            service.clear();
            productCache.clear();
            if (writeOutcomeAmbiguous(value)) {
              recoveryRequired = true;
              return readCart().catch(function () { return null; }).then(function (cartAfterAmbiguous) {
                return { kind: 'write_ambiguous', value: value, cart: cartAfterAmbiguous };
              });
            }
            return readCart().then(function (confirmedCart) {
              return { kind: 'written', action: decision.action, value: value, cart: confirmedCart, degraded: decision.degraded === true };
            }).catch(function () {
              recoveryRequired = true;
              return { kind: 'written_unconfirmed', action: decision.action, value: value, degraded: decision.degraded === true };
            });
          }).catch(function (error) {
            service.clear();
            productCache.clear();
            var businessError = businessWriteError(error);
            var beforeSignature = cartWriteSignature(cart);
            return readCart().then(function (cartAfterError) {
              var afterSignature = cartWriteSignature(cartAfterError);
              if (businessError && beforeSignature !== null && beforeSignature === afterSignature) {
                return { kind: 'write_rejected', error: error, cart: cartAfterError, message: businessError.message };
              }
              recoveryRequired = true;
              return { kind: 'write_ambiguous', error: error, cart: cartAfterError };
            }).catch(function () {
              recoveryRequired = true;
              return { kind: 'write_ambiguous', error: error };
            });
          });
        });
      }).catch(function () { return { kind: 'retry' }; });
    }

    function execute(request, writeFn) {
      var task = queue.then(function () { return run(request, writeFn); }, function () { return run(request, writeFn); });
      queue = task.catch(function () {});
      return task;
    }

    function reviewCart() {
      service.clear();
      productCache.clear();
      return readCart().then(classifyCart);
    }

    return {
      execute: execute,
      readCart: readCart,
      classifyCart: classifyCart,
      reviewCart: reviewCart,
      clear: function () { service.clear(); productCache.clear(); },
      recoveryRequired: function () { return recoveryRequired; },
      config: config
    };
  }

  function parseManifest(node) {
    var data = parseJSON(node && node.textContent);
    if (!data || !data.id || !Array.isArray(data.variants)) return null;
    var variants = Object.create(null);
    data.variants.forEach(function (variant) {
      if (!variant || !variant.id) return;
      variants[text(variant.id)] = normalizeInput({
        productId: data.id, variantId: variant.id, handle: data.handle,
        tracked: variant.tracked, policy: variant.policy,
        preorder: false, giftCard: data.giftCard, requiresShipping: variant.requiresShipping
      });
    });
    return { id: text(data.id), handle: text(data.handle), variants: variants };
  }

  function inputFromElement(element, variantId, manifests) {
    var data = element && element.dataset || {};
    var id = text(variantId || data.mdvVariantId || element && element.value);
    var productId = text(data.mdvProductId);
    var manifest = productId && manifests[productId];
    if (!manifest) {
      Object.keys(manifests).some(function (key) {
        if (manifests[key].variants[id]) { manifest = manifests[key]; return true; }
        return false;
      });
    }
    var fromManifest = manifest && manifest.variants[id];
    if (fromManifest) {
      var copy = Object.assign({}, fromManifest);
      copy.preorder = data.mdvPreorder === 'true';
      return copy;
    }
    return normalizeInput({
      productId: productId, variantId: id, handle: data.mdvHandle,
      tracked: data.mdvTracked, policy: data.mdvPolicy,
      preorder: data.mdvPreorder === 'true', giftCard: data.mdvGiftCard === 'true',
      requiresShipping: data.mdvRequiresShipping
    });
  }

  function mount(win, doc) {
    var config = normalizeConfig(parseJSON(doc.querySelector(CONFIG_SELECTOR) && doc.querySelector(CONFIG_SELECTOR).textContent));
    if (!config || typeof win.fetch !== 'function') return null;
    var manifests = Object.create(null);
    Array.prototype.forEach.call(doc.querySelectorAll(PRODUCT_SELECTOR), function (node) {
      var manifest = parseManifest(node);
      if (manifest) manifests[manifest.id] = manifest;
    });
    var gate = createGate(config, {
      fetch: win.fetch.bind(win), now: Date.now,
      AbortController: win.AbortController,
      rootPath: win.Shopify && win.Shopify.routes && win.Shopify.routes.root || '/'
    });
    gate.inputFromElement = function (element, variantId) { return inputFromElement(element, variantId, manifests); };
    gate.collectionProperty = COLLECTION_PROPERTY;
    gate.resetOwnedAddButton = resetOwnedAddButton;
    gate.label = function (key, fallback) { return text(config.labels[key]) || fallback; };
    win.MDVInventoryGate = gate;
    return gate;
  }

  return {
    normalizeConfig: normalizeConfig,
    normalizeInput: normalizeInput,
    classifyStock: classifyStock,
    responseMap: responseMap,
    requestUrl: requestUrl,
    createStockService: createStockService,
    variantFromProductJson: variantFromProductJson,
    collectionProperty: collectionProperty,
    cartMode: cartMode,
    decideCompatibility: decideCompatibility,
    writeOutcomeAmbiguous: writeOutcomeAmbiguous,
    businessWriteError: businessWriteError,
    cartWriteSignature: cartWriteSignature,
    resetOwnedAddButton: resetOwnedAddButton,
    createGate: createGate,
    parseManifest: parseManifest,
    inputFromElement: inputFromElement,
    mount: mount,
    COLLECTION_PROPERTY: COLLECTION_PROPERTY
  };
});
